import {
    concatClassNames,
    getStyleVariantClass,
} from "@thelabnyc/thelabui/src/utils/styles";

import type { ClassNames as CocoonClassNames } from "../styles/composable/forms.cocoon.module.scss";
import styles from "../styles/composable/forms.module.scss";
import type { ClassNames as SealyClassNames } from "../styles/composable/forms.sealy.module.scss";
import type { ClassNames as ToppersClassNames } from "../styles/composable/forms.toppers.module.scss";

/**
 * Classes that exist only in a site variant of the forms module. `styles` is
 * already the site's variant at module load (the build swaps the import), so
 * each resolves to the hashed class on that site and `undefined` elsewhere.
 * The keys are typed against the variant `.d.ts` files: renaming a class in a
 * variant is a compile error here, not a per-site visual regression.
 */
const variantClass = (
    key: CocoonClassNames | SealyClassNames | ToppersClassNames,
): string | undefined => getStyleVariantClass<string>(styles, key);

/** sealy: `type="checkbox"` fields. */
export const FORM_FIELD_CHECKBOX_STYLE = variantClass("formFieldCheckbox");
/** cocoon: `type="radio-choice"` fields (FormRadioSelect). */
export const FORM_FIELD_RADIO_CHOICE_STYLE = variantClass(
    "formFieldRadioChoice",
);
/** cocoon: the radio-option `<fieldset>`. */
const FORM_RADIO_OPTIONS_STYLE = variantClass("formRadioOptions");
/** toppers: radio-option `<label>` (excluded from the required marker). */
const FORM_RADIO_OPTION_LABEL_STYLE = variantClass("formRadioOptionLabel");
/** cocoon: checked radio-option `<label>`. */
const FORM_RADIO_OPTION_LABEL_CHECKED_STYLE = variantClass(
    "formRadioOptionLabelChecked",
);

// Class constants shared by the form components and hand-written form markup.
// A constant that still pairs its module class with a literal `form__*` hook
// does so because legacy contextual rules (`.checkout .form__field`,
// `.form__field--<name>`) still target that hook; each literal comes off here
// as its owning ticket (T3.4 checkout/basket, T3.5 financing, T4.x site pages)
// tears those rules down.

/** `<form>` root; the module scopes label and radio-option rules under it. */
export const FORM_CLASS_NAME = concatClassNames([styles.form, "form"]);

export const FORM_FIELD_CLASS_NAME = concatClassNames([
    styles.formField,
    "form__field",
]);

export const FORM_FIELD_HAS_ERRORS_CLASS_NAME = styles.formFieldHasErrors;

// The full submit-field pair: the submit modifier never appears without its
// base, so this one constant carries both module classes plus both literal
// hooks — call sites apply it alone, never with FORM_FIELD_CLASS_NAME.
export const FORM_FIELD_SUBMIT_CLASS_NAME = concatClassNames([
    styles.formField,
    "form__field",
    styles.formFieldSubmit,
    "form__field--submit",
]);

export const FORM_FIELD_REQUIRED_CLASS_NAME = concatClassNames([
    styles.formFieldRequired,
    "form__field--required",
]);

export const FORM_FIELD_FOCUSED_CLASS_NAME = concatClassNames([
    styles.formFieldFocused,
    "form__field--focused",
]);

export const FORM_FIELD_NOT_BLANK_CLASS_NAME = concatClassNames([
    styles.formFieldNotBlank,
    "form__field--not-blank",
]);

export const FORM_FIELD_HIDDEN_CLASS_NAME = styles.formFieldHidden;

export const FORM_LABEL_CLASS_NAME = concatClassNames([
    styles.formLabel,
    "form__label",
]);

// Radio-option markup shared by FormRadioSelect and hand-written/styleguide
// markup demonstrating it.
export const FORM_RADIO_OPTIONS_CLASS_NAME = concatClassNames([
    FORM_RADIO_OPTIONS_STYLE,
    "form__radio-options",
]);

export const FORM_RADIO_OPTION_CLASS_NAME = concatClassNames([
    styles.formRadioOption,
    "form__radio-option",
]);

export const FORM_RADIO_OPTION_LABEL_CLASS_NAME = concatClassNames([
    FORM_RADIO_OPTION_LABEL_STYLE,
    "form__radio-option__label",
]);

export const FORM_RADIO_OPTION_LABEL_CHECKED_CLASS_NAME = concatClassNames([
    FORM_RADIO_OPTION_LABEL_CHECKED_STYLE,
    "form__radio-option__label--checked",
]);

// Label placements with styling of their own in the module. `FormLabel`
// selects one from `labelPlacement`; hand-written markup applies it directly.
export const FORM_LABEL_DYNAMIC_CLASS_NAME = concatClassNames([
    styles.formLabelDynamic,
    "form__label--dynamic",
]);

export const FORM_LABEL_OUTSIDE_CLASS_NAME = styles.formLabelOutside;

/**
 * Default class for the `<input>` rendered by the text-like field components
 * (FormInput, FormNumber, FormInputWithoutFormatting, FormPasswordInput).
 */
export const FORM_FIELD_INPUT_CLASS_NAME = concatClassNames([
    styles.formFieldInput,
    "form__field__input",
]);

export const FORM_HELP_TEXT_CLASS_NAME = concatClassNames([
    styles.formHelpText,
    "form__help-text",
]);

export const FORM_ERROR_LIST_CLASS_NAME = concatClassNames([
    styles.formErrorList,
    "form__error-list",
]);

export const FORM_ERROR_CLASS_NAME = concatClassNames([
    styles.formError,
    "form__error",
]);

// DOM-query selectors for behavior hooks (focus/scroll/decorate). The module
// class, not the literal hook, so the behavior survives the literal-hook
// teardown; exported beside the classes so this file stays the single owner
// of the class contract.
export const FORM_ERROR_SELECTOR = `.${styles.formError}`;

export const FORM_FIELD_HAS_ERRORS_SELECTOR = `.${styles.formFieldHasErrors}`;

// Floating-label widget. Kept as separate co-applied classes, as in the
// module: its sibling selectors need `.floatingLabel.usingPlaceholderShown`
// on one element, and `usingValid` is an alternative modifier.
export const FLOATING_LABEL_CLASS_NAME = concatClassNames([
    styles.floatingLabel,
    "floating-label",
]);

// The literal stays for toppers: `page-specific/_home-page.scss` floats the
// `.email-sign-up` label off `.floating-label.using-placeholder-shown` (T4.x).
export const FLOATING_LABEL_USING_PLACEHOLDER_SHOWN_CLASS_NAME =
    concatClassNames([styles.usingPlaceholderShown, "using-placeholder-shown"]);

export const FLOATING_LABEL_USING_VALID_CLASS_NAME = concatClassNames([
    styles.usingValid,
    "using-valid",
]);

export const PSEUDO_PLACEHOLDER_CLASS_NAME = concatClassNames([
    styles.pseudoPlaceholder,
    "pseudo-placeholder",
]);
